import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CompanyNewInterface } from "../../../../models/GeneralModule/CompanyNews";
import companyNewService from "../../../../services/general/companyNewService";
import { CACHE_KEY_COMPANYNEWS } from "../../../../utils/constants";

interface UpdateCompanyNewContext {
  previousCompanyNews: CompanyNewInterface[];
}

const useUpdateCompanyNews = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CompanyNewInterface,
    Error,
    CompanyNewInterface,
    UpdateCompanyNewContext
  >({
    mutationFn: async (companyNew: CompanyNewInterface) => {
      // Assuming the ID is stored in the companyNew object or can be derived
      const { id, ...companyNewData } = companyNew;
      return companyNewService.Update(id || "", companyNewData);
    },

    onMutate: async (updatedCompanyNew: CompanyNewInterface) => {
      await queryClient.cancelQueries({
        queryKey: [CACHE_KEY_COMPANYNEWS],
      });

      const previousCompanyNews =
        queryClient.getQueryData<CompanyNewInterface[]>([
          CACHE_KEY_COMPANYNEWS,
        ]) || [];

      queryClient.setQueryData<CompanyNewInterface[]>(
        [CACHE_KEY_COMPANYNEWS],
        (companyNews = []) =>
          companyNews.map((companyNew) =>
            companyNew.id === updatedCompanyNew.id
              ? updatedCompanyNew
              : companyNew
          )
      );

      return { previousCompanyNews };
    },

    onSuccess: (savedCompanyNew) => {
      queryClient.setQueryData<CompanyNewInterface[]>(
        [CACHE_KEY_COMPANYNEWS],
        (companyNews) =>
          companyNews?.map((companyNew) =>
            companyNew.id === savedCompanyNew.id ? savedCompanyNew : companyNew
          )
      );
    },

    onError: (error, updatedCompanyNew, context) => {
      if (!context) return;

      queryClient.setQueryData<CompanyNewInterface[]>(
        [CACHE_KEY_COMPANYNEWS],
        context.previousCompanyNews
      );
    },
  });
};

export default useUpdateCompanyNews;
